import { Grid, withStyles, WithStyles } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { equals } from 'ramda';
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getAssetsImagesOptions, getInitialValues, validationSchema } from './config';
import { styles } from './styles';

import { PageLoader } from '~components';
import { FullHeightPage } from '~hocs';
import { AssetsImage, AssociateImages, PermissionName } from '~models';
import { boundActions, selectors } from '~store';
import { AddMoreButton, DeleteButton, EditableFormPanel, FormAutocompleteField, FormContent, Placeholder, Required } from '~ui-kit';

type AssociateImagesSettingsProps = WithStyles<typeof styles> & {
  buId: number;
  unitId: number;
};

const FormWrapper = FullHeightPage(Form);

export const AssociateImagesSettings = withStyles(styles)(
  ({ classes, buId, unitId }: AssociateImagesSettingsProps) => {
    const pagination = useSelector(selectors.page.getPagePagination, equals);
    const images = useSelector(selectors.assets.getImagesList, equals);
    const associatedImages = useSelector(selectors.assets.getAssociatedImages(unitId), equals);
    const hasPermissions = useSelector(selectors.auth.hasPermissions(PermissionName.SUBSCRIBERS_IMAGE_ASSIGN), equals);
    const { list: isLoading } = useSelector(selectors.assets.getAssetsLoaders, equals);
    const imagesOptions = getAssetsImagesOptions(images as AssetsImage[]);
    const initialValues = getInitialValues(associatedImages as AssociateImages);
    const [isEditing, setEditing] = useState<boolean>(false);

    useEffect(() => {
      boundActions.assets.fetchAssetsImagesListInit({ pagination, allList: true });
      boundActions.assets.fetchAssociatedImagesInit({ buId, unitId });
    }, []);

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={values => {
          boundActions.assets.updateAssociatedImagesInit({
            buId,
            unitId,
            values,
          });
          setEditing(false);
        } }
      >
        {({ values, resetForm, setFieldValue, handleSubmit }) =>
          <FormWrapper>
            <EditableFormPanel
              title="Image Associations"
              isEditing={isEditing}
              onChange={setEditing}
              onReset={resetForm}
              onSubmit={handleSubmit}
              isHidden={!hasPermissions}
              disabled={isLoading || !imagesOptions.length}
            >
              {isLoading ? <PageLoader /> :
                <FormContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {values.imageIds.map((imageId, index) => (
                        <Fragment key={index}>
                          <Grid container spacing={0}>
                            <Grid item xs={12} className={classes.pulldown}>
                              <FormAutocompleteField
                                label={isEditing ? <Required>Image Name</Required> : `Image ${index + 1}`}
                                options={imagesOptions}
                                name={`imageIds[${index}]`}
                                showing={!isEditing}
                                className={classes.field}
                              />
                              {isEditing &&
                              <DeleteButton
                                className={classes.delete}
                                onClick={() => setFieldValue('imageIds', values.imageIds.filter((v, i) => i !== index))}
                              />}
                            </Grid>
                            <Grid item xs={12} className={classes.notes}>
                              {imagesOptions.filter(im => imageId === im.value)[0]?.optional}
                            </Grid>
                          </Grid>
                        </Fragment>
                      ))}
                    </Grid>
                    {!isEditing && values.imageIds.length < 1 && <Placeholder title="No Images" />}
                    {isEditing && values.imageIds.length < imagesOptions.length &&
                    <Grid item xs={12} className={classes.add}>
                      <AddMoreButton
                        onClick={() => setFieldValue(`imageIds[${values.imageIds.length}]`, '')}
                      >
                        Add Image
                      </AddMoreButton>
                    </Grid>
                    }
                  </Grid>
                </FormContent>
              }
            </EditableFormPanel>
          </FormWrapper>
        }
      </Formik>
    );
  });
